import { useEffect, useState } from "react";
import { 
    ActionStatusHelper, 
    DomainType, 
    Entity, 
    FeedBackType, 
    MediaData, 
    MediaURL, 
    MessageHelper, 
    UploadedMedia, 
    useAppNavigation, 
    LONG_PROCESS_TOAST_DURATION, 
    UIFeedbackHooks, 
    ModalHook,
    GenericDomainDetail,
    CreateAction,
    UpdateAction
} from "@/Shared";

import { AccessRightModel } from "@/Domains";

export interface SubmitParams<T extends Entity> {
    domainType: DomainType,
    domainLabel: string,
    accessRight: AccessRightModel,
    uiFeedbackHooks: UIFeedbackHooks,
    createAction : CreateAction<T>,
    updateAction : UpdateAction<T>,
    entityId?: number | string,
    onSuccess?: (entity: T) => Promise<void>,
    redirectOnSuccess?: boolean,
    inModalHook? : ModalHook
}

/**
 * useFormSubmit hook – manages entity form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises entity form data by fetching current entity and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current entity (if editing) and the list of agents
 * - Automatically switches between `createEntity` or `updateEntity` based on entity presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const useFormSubmit = <T,>({
    domainType,
    domainLabel,
    accessRight,
    uiFeedbackHooks,
    createAction,
    updateAction,
    entityId,
    onSuccess,
    redirectOnSuccess = true,
    inModalHook
}: SubmitParams<any>
) => {

    const { toastHook, alertHook } = uiFeedbackHooks;

    const { goToDetail } = useAppNavigation();

    const [formId, setFormId] = useState<number | string | undefined>(entityId);

    const [idempotencyKey] = useState(() => 
        sessionStorage.getItem('idempotency_key:' + domainType) ?? crypto.randomUUID()
    );

    const formResponse = formId ? updateAction.response : createAction.response;

    const openToast = (message: string, type: FeedBackType, onClose?: () => void, duration?: number) => {
        toastHook.openToast({ data: { message, type, duration }, onClose });
    };

    useEffect(() => {
        sessionStorage.setItem('idempotency_key:' + domainType, idempotencyKey)
    }, [idempotencyKey]);


    useEffect(() => {
        const { isLoading, isFailed, isSucceeded } = ActionStatusHelper.is(formResponse.status);

        if (isSucceeded) {
            openToast(
                formId ? MessageHelper.EDITING.SUCCESS(domainLabel) : MessageHelper.CREATING.SUCCESS(domainLabel),
                FeedBackType.SUCCESS,
                //() => formId ? goToDetail(domainType, formId) : goToLayout(domainType)
            );

        }
        else if (isFailed) {
            openToast(
                formResponse.error ?? "",
                FeedBackType.ERROR,
                () => alertHook?.openAlert({
                    message: formResponse.error ?? "",
                    type: FeedBackType.ERROR
                })
            );
        }
        else if (isLoading) {
            toastHook.openToast({
                data: {
                    message: "Soumission en cours...",
                    type: FeedBackType.INFO,
                    duration: LONG_PROCESS_TOAST_DURATION
                }
            });
        }
    }, [formResponse.status, formResponse.error, formId]);



    /**
     * handleSubmit – Handles form submission for creating or updating a entity.
     * 
     * Logic:
     * - If `currentEntity` has an `id`, it assumes the form is in edit mode and calls `updateEntity`
     * - Otherwise, it creates a new entity via `createEntity`
     * 
     * @param values - The form values entered by the user
     */
    const handleSubmit = async (values: Record<string, any>) => {
        if (!accessRight.canWrite(domainType)) {
            console.warn("Unauthorized create/update attempt on :" + domainType);
            return;
        }

        openToast("Préparation pour envoi...", FeedBackType.INFO);

        const formData = new FormData();
        appendToFormData(formData, values);

        const id = entityId ?? values.id;
        setFormId(id);

        const entity = id
            ? await updateAction.execute?.({ id, values: formData})
            : await createAction.execute?.(formData, idempotencyKey);

        if (entity) { 
            successHandler(entity); 
        }
    };

    const successHandler = async <T extends Entity>(entity: T) => {
        if (onSuccess) {
            await onSuccess(entity);
        }

        
        if(inModalHook) {
            inModalHook.openModal({
                title: `Détail de ${domainLabel}`,
                content: <GenericDomainDetail
                    domainType={domainType}
                    inModalHook={inModalHook}
                    extraArgs={{
                        entity
                    }}
                />
            })
        }
        else if (redirectOnSuccess) {
            goToDetail(domainType, entity.id as number)
        }
    }



    const appendToFormData = (
        formData: FormData,
        value: any,
        key?: string
    ) => {
        if (value === null || value === undefined) return;

        // Media handling (TON cas métier)
        if (value instanceof MediaData && key) {
            formData.append(`${key}[id]`, String(value.id || ""));
            formData.append(`${key}[small]`, value.small || "");
            formData.append(`${key}[caption]`, value.caption || "");
            formData.append(`${key}[type]`, value.type);

            if (value instanceof UploadedMedia) {
                formData.append(`${key}[large]`, value.large);
            } else if (value instanceof MediaURL) {
                formData.append(`${key}[url]`, value.url);
            }
            return;
        }

        // File
        if (value instanceof File && key) {
            formData.append(key, value);
            return;
        }

        // Array
        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                appendToFormData(formData, item, `${key}[${index}]`);
            });
            return;
        }

        // Object (✅ le point clé)
        if (typeof value === "object") {
            Object.entries(value).forEach(([childKey, childValue]) => {
                appendToFormData(
                    formData,
                    childValue,
                    key ? `${key}[${childKey}]` : childKey
                );
            });
            return;
        }

        // Primitive
        if (key) {
            formData.append(key, String(value));
        }
    };

    return {
        // Form
        handleSubmit,
        formResponse
    }
}